import React from "react";
import CaseStudy from "../../components/caseStudy/CaseStudy";
import ImmoFlowApp from "../../assets/immoflow.png";
import ImmoFlowAgencies from "../../assets/immoflow-agencies.png";
import ImmoFlowUsers from "../../assets/immoflow-users.png";
import ImmoFlowAgencyDashboard from "../../assets/immoflow-agency-dashboard.png";
import ImmoFlowProspects from "../../assets/immoflow-prospects.png";
import ImmoFlowAppointments from "../../assets/immoflow-appointments.png";
import ImmoFlowCalendar from "../../assets/immoflow-calendar.png";
import ImmoFlowMessages from "../../assets/immoflow-messages.png";
import { FaReact, FaNodeJs, FaGitAlt } from "react-icons/fa";
import { SiExpress, SiMongodb, SiRedux, SiTailwindcss, SiChartdotjs, SiCloudinary } from "react-icons/si";
import { MdAutoAwesome } from "react-icons/md";

// Content only — the layout lives in components/caseStudy/CaseStudy.jsx.
const study = {
  slug: "immoflow",
  title: "ImmoFlow",
  subtitle:
    "A multi-agency real estate management platform: property listings, a prospect pipeline, appointments, internal messaging and a brandable agency website — in one role-aware dashboard.",
  badges: [
    { label: "Real Estate Management Platform" },
    { label: "Multi-agency, role-based", icon: <MdAutoAwesome size={13} /> },
  ],
  tags: ["React", "Redux Toolkit", "Node.js", "Express", "MongoDB", "Tailwind CSS"],
  facts: [
    { label: "Type", value: "Real estate agency management platform" },
    { label: "Users", value: "Five roles, from platform super admin to independent agent" },
    { label: "Architecture", value: "React SPA + Express REST API + MongoDB" },
    { label: "Interface language", value: "French" },
  ],
  demoUrl: "https://app.arcade.software/share/W8j7S9GsjVRuPtXDJ19K",
  media: [
    { type: "image", src: ImmoFlowApp, label: "Platform dashboard", alt: "ImmoFlow super admin dashboard with platform statistics and charts" },
    { type: "image", src: ImmoFlowAgencyDashboard, label: "Agency dashboard", alt: "ImmoFlow agency dashboard with property, prospect and team figures" },
  ],

  overview: {
    title: "What is ImmoFlow?",
    paragraphs: [
      "ImmoFlow gives a real estate agency one place to run its day-to-day work: publish and manage properties, follow each prospect from first contact to a closed deal, schedule viewings, message colleagues, and customise the agency's own showcase website.",
      "It is built for several agencies on the same platform. A super admin manages the agencies themselves, each agency manages its own team, and every agent sees the data that belongs to them — with a dashboard that adapts to whoever is signed in.",
    ],
  },
  problem: {
    paragraphs: [
      "A small agency typically runs on a mix of spreadsheets, a shared calendar, a messaging app and a website somebody else maintains. Listings, clients and viewings live in different places, so nobody has the full picture and a prospect can easily go cold between two tools.",
      "ImmoFlow puts those pieces in one application, and treats two things as requirements from the start: an agency must never see another agency's data, and each role should see a dashboard that is relevant to its job rather than one generic screen.",
    ],
  },
  solution: {
    steps: [
      "React SPA — Redux Toolkit + React Query",
      "Express REST API — Helmet, rate limiting, validation",
      "JWT authentication + role authorisation",
      "Agency scoping on every query",
      "MongoDB (Mongoose) · Cloudinary for images",
    ],
    note:
      "Every request passes through the same chain: authentication, a role check, then an agency scope that is applied on the server — the frontend hides what a role cannot use, but the API is what enforces it.",
  },

  features: [
    {
      title: "Property Management",
      description: "Create, edit and filter listings with type, price, address, features and up to ten photos, uploaded to Cloudinary. Each property keeps its own list of inquiries.",
    },
    {
      title: "Prospect Pipeline",
      description: "Every prospect carries a budget, preferences, notes and a status that moves from new through contacted, qualified, proposal and negotiation to won or lost. An inquiry on a property creates the prospect automatically.",
    },
    {
      title: "Appointments & Calendar",
      description: "Viewings, calls and meetings are linked to a prospect, an agent and optionally a property, shown in a calendar view. The API refuses a slot that overlaps another appointment for the same agent.",
    },
    {
      title: "Role-Adaptive Dashboard",
      description: "One statistics endpoint serves every role: platform-wide figures for the super admin, agency figures for managers, personal figures for agents — with six-month trends charted in Chart.js.",
    },
    {
      title: "Five Roles, One Platform",
      description: "Super admin, agency admin, agency, agent and independent mandataire, each with its own permissions, enforced both in the React routes and in the API.",
    },
    {
      title: "Agency Website & CMS",
      description: "Each agency sets its logo, colours, font, texts and custom pages; the showcase site and its property pages are rendered from those settings.",
    },
    {
      title: "Guided Chatbot Assistant",
      description: "A rule-based assistant that answers how-to questions about the app and runs a step-by-step property search (city, budget, type, rooms, area, features), then ranks listings against the answers. No external AI service involved.",
    },
    {
      title: "Internal Messaging & Notifications",
      description: "Messages between colleagues with priorities and unread counts, plus in-app notifications when an appointment is created or changed or a message arrives.",
    },
    {
      title: "Support Tickets",
      description: "Agency users report problems from inside the app; the platform's super admin follows them from open to resolved.",
    },
  ],

  gallery: [
    { src: ImmoFlowProspects, caption: "Prospects — the pipeline list on the left, and the selected prospect's budget, search criteria, agent and notes on the right." },
    { src: ImmoFlowAppointments, caption: "Appointments, agenda view — grouped by day, with the prospect, agent, property and location on each row and the status editable in place." },
    { src: ImmoFlowCalendar, caption: "Appointments, calendar view — a month grid with one coloured dot per appointment status." },
    { src: ImmoFlowMessages, caption: "Internal messaging — the conversation list and the open message, with priority and type." },
    { src: ImmoFlowUsers, caption: "Users — every account with its role, agency and status, filterable by role or agency." },
    { src: ImmoFlowAgencies, caption: "Agencies — the platform admin creates, activates and edits agencies from one grid." },
  ],
  galleryNote: "Screenshots use fictional seed data. Select one to enlarge it.",

  diagrams: [
    {
      label: "Request flow",
      steps: [
        "React client (Axios)",
        "Rate limiter + Helmet",
        "JWT check — cookie or Bearer token",
        "Role authorisation + agency scope",
        "Route handler → Mongoose → MongoDB",
      ],
    },
    {
      label: "From inquiry to appointment",
      steps: [
        "Inquiry on a property",
        "Prospect created and assigned to the listing's agent",
        "Appointment request",
        "Conflict check + same-agency check",
        "Appointment saved → notification to the agent",
      ],
    },
  ],

  challenges: [
    {
      title: "Keeping each agency's data separate",
      problem: "Several agencies share one database, so a missing filter in a single route would show one agency's clients to another.",
      solution: "Every agency-owned document stores its agency, and a shared middleware compares the requested agency with the signed-in user's: a mismatch is rejected, and when no agency is sent, the user's own is applied on the server rather than trusted from the client.",
    },
    {
      title: "One dashboard for five roles",
      problem: "A super admin, an agency manager and an agent need different numbers, and building a separate dashboard for each would triple the code.",
      solution: "A single endpoint derives its filters from the user's role — everything, the agency, or only the agent's own records — then runs the counts in parallel and returns the same shape, so the frontend renders one dashboard with different data.",
    },
    {
      title: "Double-booked agents",
      problem: "Two people scheduling viewings for the same agent at overlapping times is an easy mistake that the calendar alone does not prevent.",
      solution: "Before an appointment is saved, the API looks for an overlapping appointment for that agent and refuses the request with a clear message; it also checks that the prospect and the agent belong to the same agency.",
    },
    {
      title: "A useful chatbot without an AI bill",
      problem: "An assistant that helps users find a property or learn the app is valuable, but a paid language-model API was out of scope.",
      solution: "The assistant is a small rule-based engine in the browser: intent detection over French and English keywords, a guided search that extracts city, budget and features from free text, and a scoring function that ranks properties against the answers.",
    },
    {
      title: "Trends over months, not just totals",
      problem: "Counting documents gives totals, but a dashboard needs to show how listings and prospects evolve — including months where nothing happened.",
      solution: "MongoDB aggregation groups records by year and month, and the API fills the missing months with zero so every chart has a continuous six-month series.",
    },
  ],

  stack: [
    { group: "Frontend", items: ["React 18", "React Router", "Redux Toolkit", "React Query", "React Hook Form", "Tailwind CSS", "Headless UI"] },
    { group: "Data visualisation", items: ["Chart.js", "react-chartjs-2", "date-fns"] },
    { group: "Backend", items: ["Node.js", "Express", "REST API", "express-validator"] },
    { group: "Database & storage", items: ["MongoDB", "Mongoose", "Cloudinary (images)", "Multer"] },
    { group: "Security", items: ["JWT", "bcrypt password hashing", "Helmet", "Rate limiting", "Hashed, expiring password-reset tokens"] },
    { group: "Tools", items: ["Git"] },
  ],
  stackIcons: [
    [FaReact, "React"],
    [SiRedux, "Redux Toolkit"],
    [SiTailwindcss, "Tailwind CSS"],
    [SiChartdotjs, "Chart.js"],
    [FaNodeJs, "Node.js"],
    [SiExpress, "Express"],
    [SiMongodb, "MongoDB"],
    [SiCloudinary, "Cloudinary"],
    [FaGitAlt, "Git"],
  ],
};

const ImmoFlowCaseStudy = () => <CaseStudy study={study} />;

export default ImmoFlowCaseStudy;
